class CollectionGridLayout {
  constructor() {
    this.init();
  }

  init() {
    // Ejecutar cuando el DOM esté listo
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.applyProductFormats());
    } else {
      this.applyProductFormats();
    }

    // Re-evaluar cuando se carguen nuevos productos (infinite scroll)
    window.addEventListener('infiniteScroll:loaded', () => {
      this.applyProductFormats();
    });

    // Re-evaluar cuando se eliminen banners duplicados
    window.addEventListener('collection:layoutChanged', () => {
      this.applyProductFormats();
    });

    // Re-evaluar cuando se apliquen filtros
    document.addEventListener('facets:change', () => {
      // Usar setTimeout para esperar a que el DOM se actualice completamente
      setTimeout(() => {
        // Re-configurar el observer porque el DOM se reemplazó
        this.observeGridChanges();
        // Aplicar formatos
        this.applyProductFormats();
      }, 200);
    });

    // Observer para detectar cambios en data-columns
    this.observeGridChanges();

    // Re-evaluar cuando se redimensiona la ventana (móvil <-> desktop)
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.applyProductFormats();
      }, 250);
    });
  }

  /**
   * Detecta items "huérfanos" que quedan solos en una fila.
   * Casos detectados (móvil y desktop - los formatos dobles aplican en ambos):
   * 1. Producto simple seguido de producto doble (mala configuración en backoffice)
   * 2. Producto simple + producto OOS + producto doble (OOS oculto deja hueco)
   * 3. Producto OOS + producto simple + producto doble (OOS a la izquierda)
   * 4. Último producto solo en su fila cuando no hay más productos por cargar
   *
   * @param {Array} allItems - Todos los items del grid (incluyendo OOS)
   * @param {Array} visibleItems - Solo los items visibles (sin OOS)
   * @param {boolean} isDesktop - Si estamos en desktop (>768px)
   */
  detectOrphanItems(allItems, visibleItems, isDesktop) {
    const orphanItems = new Set();

    // Función para determinar si un formato ocupa toda la fila (es "doble")
    // NOTA: Estos formatos usan grid-column: 1 / -1 tanto en móvil como en desktop
    const isDoubleFormat = (format) => {
      return format === 'panoramica_actual' ||
             format === 'panoramica_grande' ||
             format === 'vertical_grande';
    };

    // Función para obtener el formato original de un item (sin modificaciones previas)
    const getOriginalFormat = (item) => {
      return item.dataset.originalFormat || item.getAttribute('data-format');
    };

    // Casos 1, 2, 3: Aplican tanto en móvil como en desktop
    // (los formatos dobles usan grid-column: 1 / -1 en todas las resoluciones)

    // Recorrer items visibles y calcular su posición en el grid
    let currentColumn = 0; // 0 = izquierda, 1 = derecha

    for (let i = 0; i < visibleItems.length; i++) {
      const item = visibleItems[i];
      const format = getOriginalFormat(item);
      const isDouble = isDoubleFormat(format);

      // Guardar la columna donde empieza este item
      const itemColumn = currentColumn;

      // Actualizar columna para el siguiente
      if (isDouble) {
        // Los dobles ocupan toda la fila, el siguiente empieza en columna 0
        currentColumn = 0;
      } else {
        // Los simples alternan
        currentColumn = currentColumn === 0 ? 1 : 0;
      }

      // Detectar huérfano: item simple en columna izquierda seguido de un doble
      if (!isDouble && itemColumn === 0) {
        const nextItem = visibleItems[i + 1];
        if (nextItem) {
          const nextFormat = getOriginalFormat(nextItem);
          if (isDoubleFormat(nextFormat)) {
            // Este item queda solo antes de un doble → huérfano
            orphanItems.add(item);
          }
        }
      }
    }

    // También detectar huérfanos causados por productos OOS
    // Recalcular considerando TODOS los items (incluyendo OOS)
    currentColumn = 0;
    const allItemsPositions = new Map();

    allItems.forEach((item) => {
      const format = getOriginalFormat(item);
      const isDouble = isDoubleFormat(format);
      const isOOS = item.classList.contains('product-out-of-stock');

      allItemsPositions.set(item, { column: currentColumn, isDouble, isOOS });

      if (isDouble) {
        currentColumn = 0;
      } else {
        currentColumn = currentColumn === 0 ? 1 : 0;
      }
    });

    // Buscar casos donde un visible queda solo por OOS adyacente(s) + doble siguiente
    // Soporta múltiples OOS consecutivos entre el simple y el doble
    for (let i = 0; i < allItems.length; i++) {
      const item = allItems[i];
      const pos = allItemsPositions.get(item);

      // Saltar si es OOS o doble
      if (pos.isOOS || pos.isDouble) continue;

      // Buscar el siguiente item VISIBLE (saltando todos los OOS)
      let nextVisibleIndex = i + 1;
      while (nextVisibleIndex < allItems.length) {
        const nextItem = allItems[nextVisibleIndex];
        const nextPos = allItemsPositions.get(nextItem);
        if (!nextPos.isOOS) break; // Encontramos un visible
        nextVisibleIndex++;
      }

      const nextVisibleItem = allItems[nextVisibleIndex];
      if (!nextVisibleItem) continue;

      const nextVisiblePos = allItemsPositions.get(nextVisibleItem);

      // Contar cuántos OOS hay entre este item y el siguiente visible
      const oosCount = nextVisibleIndex - i - 1;

      // Si el siguiente visible es doble, verificar si este item queda huérfano
      if (nextVisiblePos.isDouble) {
        if (pos.column === 0) {
          // Simple en columna izquierda con OOS después
          if (oosCount > 0) {
            orphanItems.add(item);
          }
        } else if (pos.column === 1) {
          // Simple en columna derecha con OOS a la izquierda
          const prevItem = allItems[i - 1];
          if (prevItem) {
            const prevPos = allItemsPositions.get(prevItem);
            if (prevPos.isOOS) {
              orphanItems.add(item);
            }
          }
        }
      }
    }

    // Caso 4: Último producto visible solo en su fila cuando no hay más productos por cargar
    // Solo cuando el formato es vertical_actual (llegamos tras paginaciones/infinite scroll)
    const hasMoreProducts = this.hasMoreProductsToLoad();

    if (!hasMoreProducts && visibleItems.length > 0) {
      const lastVisibleItem = visibleItems[visibleItems.length - 1];
      const lastFormat = getOriginalFormat(lastVisibleItem);

      // Solo si el último es vertical_actual en columna izquierda (item simple, no doble)
      if (!isDoubleFormat(lastFormat) && lastFormat === 'vertical_actual') {
        // Calcular en qué columna termina el último item visible
        // Los formatos dobles resetean a columna 0 (tanto en móvil como en desktop)
        let finalColumn = 0;

        for (let i = 0; i < visibleItems.length - 1; i++) {
          const format = getOriginalFormat(visibleItems[i]);
          if (isDoubleFormat(format)) {
            // Los dobles ocupan toda la fila, el siguiente empieza en columna 0
            finalColumn = 0;
          } else {
            finalColumn = finalColumn === 0 ? 1 : 0;
          }
        }

        // Si el último item está en columna 0 (izquierda), está solo
        if (finalColumn === 0) {
          orphanItems.add(lastVisibleItem);
        }
      }
    }

    return orphanItems;
  }

  /**
   * Loader visible (inline o por CSS). No usar solo style.display: sin inline,
   * el tema oculta .infinite-scroll-loading vía CSS y '' !== 'none' daba true a falsas.
   */
  isLoadingIndicatorVisible(loadingIndicator) {
    if (!loadingIndicator) return false;
    return window.getComputedStyle(loadingIndicator).display !== 'none';
  }

  /**
   * Verifica si hay más productos por cargar (paginación o infinite scroll)
   */
  hasMoreProductsToLoad() {
    const paginationWrapper = document.querySelector('.wt-pagination-wrapper');
    const loadingIndicator = document.querySelector('.infinite-scroll-loading');
    const grid = document.querySelector('#product-grid');

    // Determinar si infinite scroll está activo (paginación oculta)
    const paginationHidden = paginationWrapper && paginationWrapper.style.display === 'none';

    // CASO 1: Infinite scroll activo (paginación oculta)
    if (paginationHidden) {
      // El infinite scroll expone hasMorePages en el grid tras cada carga/paginación
      const hasMoreAttr = grid?.dataset.infiniteScrollHasMorePages;
      if (hasMoreAttr === 'false') return false;
      if (hasMoreAttr === 'true') return true;

      // Fallback: productos precargados, loading, o enlace "siguiente" en paginación oculta
      const preloadedHidden = document.querySelectorAll('.collection__grid__item.preloaded-hidden');
      if (preloadedHidden.length > 0 || this.isLoadingIndicatorVisible(loadingIndicator)) return true;
      if (paginationWrapper?.querySelector('.wt-pagination__item--prev, a[rel="next"]')) return true;

      return false;
    }

    // CASO 2: Paginación tradicional visible
    if (paginationWrapper) {
      const nextLink = paginationWrapper.querySelector('.wt-pagination__item--prev');
      if (nextLink) return true;
    }

    // Fallback: solo si el loader está realmente visible (p. ej. cargando más)
    if (this.isLoadingIndicatorVisible(loadingIndicator)) return true;

    return false;
  }

  observeGridChanges() {
    const grid = document.querySelector('#product-grid');
    if (!grid) return;
    
    // Desconectar observer anterior si existe
    if (this.gridObserver) {
      this.gridObserver.disconnect();
    }
    
    this.gridObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-columns') {
          // Usar setTimeout para asegurar que el cambio se haya aplicado completamente
          setTimeout(() => {
            this.applyProductFormats();
          }, 50);
        }
      });
    });

    this.gridObserver.observe(grid, { attributes: true });
  }

  applyProductFormats() {
    const grid = document.querySelector('#product-grid');
    if (!grid) return;

    const columns = grid.getAttribute('data-columns');

    // Obtener todos los items (incluyendo out-of-stock para detectar adyacencias)
    const allItems = Array.from(grid.querySelectorAll('.collection__grid__item'));

    // Crear un mapa de índices: posición visible -> item
    const visibleItems = [];
    const outOfStockPositions = new Map(); // Guardar posición original de out-of-stock

    allItems.forEach((item, originalIndex) => {
      if (item.classList.contains('product-out-of-stock')) {
        outOfStockPositions.set(originalIndex, item);
      } else {
        visibleItems.push(item);
      }
    });

    // Guardar formato original si no existe (para poder recalcular)
    allItems.forEach(item => {
      if (!item.dataset.originalFormat) {
        item.dataset.originalFormat = item.getAttribute('data-format') || 'vertical_actual';
      }
    });

    // Limpiar clases de huérfano anteriores
    allItems.forEach(item => {
      item.classList.remove('collection__grid__item--orphan');
    });

    // Solo aplicar en vista de 2 columnas
    if (columns !== '2') {
      // Limpiar todos los estilos inline en otras vistas
      allItems.forEach(item => {
        this.clearInlineStyles(item);
        // Restaurar formato original
        if (item.dataset.originalFormat) {
          item.setAttribute('data-format', item.dataset.originalFormat);
        }
      });
      return;
    }

    const isDesktop = window.innerWidth > 768;

    // Detectar items huérfanos
    // - En desktop: todos los casos (OOS, dobles, último solo)
    // - En móvil: solo el último producto solo (los demás se recolocan automáticamente)
    const orphanItems = this.detectOrphanItems(allItems, visibleItems, isDesktop);

    // Aplicar clase de huérfano (centra sin cambiar tamaño)
    orphanItems.forEach(item => {
      item.classList.add('collection__grid__item--orphan');
      const title = item.querySelector('.brn-card__title')?.textContent?.trim() || 'Sin título';
      console.log('[GridLayout] Huérfano detectado:', title);
    });

    // Aplicar formatos según data-format (solo en desktop)
    if (isDesktop) {
      visibleItems.forEach((item) => {
        const format = item.dataset.originalFormat || item.getAttribute('data-format');

        // Si ya es huérfano, el CSS de --orphan tiene prioridad, no aplicar vertical_grande
        if (item.classList.contains('collection__grid__item--orphan')) {
          this.clearInlineStyles(item);
          return;
        }

        // Aplicar formato según data-format configurado en backoffice
        switch (format) {
          case 'vertical_grande':
            this.applyVerticalGrandeFormat(item);
            break;
          case 'panoramica_actual':
            this.applyPanoramicaActualFormat(item);
            break;
          case 'panoramica_grande':
            this.applyPanoramicaGrandeFormat(item);
            break;
          case 'vertical_actual':
          default:
            this.clearInlineStyles(item);
            break;
        }
      });
    }
  }

  clearInlineStyles(item) {
    item.removeAttribute('style');
    
    const brn_card = item.querySelector('.brn-card');
    const card_container = item.querySelector('.card__container');
    
    if (brn_card) {
      brn_card.removeAttribute('style');
    }
    
    if (card_container) {
      card_container.removeAttribute('style');
    }
  }

  applyVerticalGrandeFormat(item) {
    // Cambiar el data-format para que el CSS lo maneje
    item.setAttribute('data-format', 'vertical_grande');
    this.clearInlineStyles(item);
  }

  applyPanoramicaActualFormat(item) {
    // El CSS ya maneja esto con data-format="panoramica_actual"
    this.clearInlineStyles(item);
  }

  applyPanoramicaGrandeFormat(item) {
    // El CSS ya maneja esto con data-format="panoramica_grande"
    this.clearInlineStyles(item);
  }
}

// Inicializar
new CollectionGridLayout();

